import {Injectable} from "@angular/core";
import {StoreCountryEnum} from "@enums/storeCountry.enum";
import {ValidatorFn, Validators} from "@angular/forms";
import {RxwebValidators} from "@rxweb/reactive-form-validators";

@Injectable({
    providedIn: 'root'
})
export class CountryService {

    private readonly regex = {
        france: {
            codePanonceau: /^\d{5}$/,
            tvaNumber: /^FR\d{11}$/,
            phone: /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
            postalCode: /^\d{5}$/,
            capitalSocial: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
        },
        luxembourg: {
            codePanonceau: /^\d{2}LU$/,
            tvaNumber: /^LU\d{8}$/,
            rcsNumber: /^B\d{4,5}$/,
            phone: /^(?:\+352\s?)?\d{6,8}$/,
            postalCode: /^L\d{4}$/,
            establishmentAuthorizations: /^\d{8} \/ \d{1,3}$/,
            capitalSocial: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
        },
        espagne: {
            nif: /^(B-\d{8})$/,
            codePanonceau: /^\d{2}SP$/,
            capitalSocial: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
            postalCode: /^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/,
        },
        portugal: {
            codePanonceau: /^\d{2}PL$/,
            postalCode: /^\d{4}-\d{3}$/,
            nif: /^\d{9}$/,
            socialCapitalAndCapitalPaidValidators: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
            equity: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
            cae: /^\d{5}$/,
        },
        slovenie: {
            codePanonceau: /^\d{2}SL$/,
            taxNumber: /^(SI{1}\s{1}\d{8})$/,
            capitalSocial: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
            postalCode: /^[1-9][0-9]{3}$/,
            ms: /^\d{8,10}$/,
        },
        andorre: {
            codePanonceau: /^\d{2}AD$/,
            postalCode: /^AD[1-7]\d{2}$/,
            phone: /^(?:\+376\s?)?[3-9]\d{2}\s?\d{3}$/,
            tvaNumber: /^(?:[A-Z]\d{6}[A-Z]|[A-Z]-\d{6}-[A-Z])$/,
            capitalSocial: /^\d{1,3}([ \u00A0\u202F]\d{3}){0,3},\d{2} €$/,
        }
    };

    private readonly emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    public getRegexTaxNumberSlovenia(): RegExp{
        return this.regex.slovenie.taxNumber;
    }

    public getRegexCodePanonceauLuxembourg(): RegExp {
        return this.regex.luxembourg.codePanonceau;
    }

    public getRegexNifSpain(): RegExp {
        return this.regex.espagne.nif;
    }

    public getRegexNifPortuguese(): RegExp {
        return this.regex.portugal.nif;
    }

    public getRegexMsSlovenia(): RegExp {
        return this.regex.slovenie.ms;
    }

    public getRegexCodePanonceauSpain(): RegExp {
        return this.regex.espagne.codePanonceau;
    }

    public getRegexCodePanonceauPortugal(): RegExp {
        return this.regex.portugal.codePanonceau;
    }

    public getRegexCodePanonceauSlovenia(): RegExp {
        return this.regex.slovenie.codePanonceau;
    }

    public getRegexCodePanonceauAndorran(): RegExp {
        return this.regex.andorre.codePanonceau;
    }

    public getRegexPostalCodeFrench(): RegExp {
        return this.regex.france.postalCode;
    }

    public getRegexPostalCodeLuxembourger(): RegExp {
        return this.regex.luxembourg.postalCode;
    }

    public getRegexPostalCodeSpain(): RegExp {
        return this.regex.espagne.postalCode;
    }

    public getRegexPostalCodePortuguese(): RegExp {
        return this.regex.portugal.postalCode;
    }

    public getRegexPostalCodeSlovenia(): RegExp {
        return this.regex.slovenie.postalCode;
    }

    public getRegexPostalCodeAndorran(): RegExp {
        return this.regex.andorre.postalCode;
    }

    public getRegexPhoneFrench(): RegExp {
        return this.regex.france.phone;
    }

    public getRegexPhoneLuxembourger(): RegExp {
        return this.regex.luxembourg.phone;
    }

    public getRegexPhoneAndorran(): RegExp {
        return this.regex.andorre.phone;
    }

    public getRegexPhone(country: StoreCountryEnum): RegExp {
        switch (country) {
            case StoreCountryEnum.FRANCE:
                return this.getRegexPhoneFrench();
            case StoreCountryEnum.LUXEMBOURG:
                return this.getRegexPhoneLuxembourger();
            case StoreCountryEnum.ANDORRE:
                return this.getRegexPhoneAndorran();
            default:
                return null;
        }
    }

    public getRegexTvaNumberFrench(): RegExp {
        return this.regex.france.tvaNumber;
    }

    public getRegexTvaNumberLuxembourger(): RegExp {
        return this.regex.luxembourg.tvaNumber;
    }

    public getRegexTvaNumberAndorran(): RegExp {
        return this.regex.andorre.tvaNumber;
    }

    public getRegexRcsNumberLuxembourger(): RegExp {
        return this.regex.luxembourg.rcsNumber;
    }

    public getRegexSocialCapitalFrench(): RegExp {
        return this.regex.france.capitalSocial;
    }

    public getRegexSocialCapitalLuxembourger(): RegExp {
        return this.regex.luxembourg.capitalSocial;
    }

    public getRegexSocialCapitalSpanish(): RegExp {
        return this.regex.espagne.capitalSocial;
    }

    public getRegexSocialCapitalPortuguese(): RegExp {
        return this.regex.portugal.socialCapitalAndCapitalPaidValidators;
    }

    public getRegexSocialCapitalSlovenian(): RegExp {
        return this.regex.slovenie.capitalSocial;
    }

    public getRegexSocialCapitalAndorran(): RegExp {
        return this.regex.andorre.capitalSocial;
    }

    public getRegexSocialCapital(country: StoreCountryEnum): RegExp {
        switch (country) {
            case StoreCountryEnum.FRANCE:
                return this.getRegexSocialCapitalFrench();
            case StoreCountryEnum.LUXEMBOURG:
                return this.getRegexSocialCapitalLuxembourger();
            case StoreCountryEnum.ESPAGNE:
                return this.getRegexSocialCapitalSpanish();
            case StoreCountryEnum.PORTUGAL:
                return this.getRegexSocialCapitalPortuguese();
            case StoreCountryEnum.SLOVENIE:
                return this.getRegexSocialCapitalSlovenian();
            case StoreCountryEnum.ANDORRE:
                return this.getRegexSocialCapitalAndorran();
            default:
                return null;
        }
    }

    public getRegexEquityPortuguese(): RegExp {
        return this.regex.portugal.equity;
    }

    public getRegexEstablishmentAuthorizationsLuxembourger(): RegExp {
        return this.regex.luxembourg.establishmentAuthorizations;
    }

    public getRegexCaePortuguese(): RegExp {
        return this.regex.portugal.cae;
    }

    public getRegexEmail(): RegExp {
        return this.emailRegex;
    }

    public getCountryWithCodePanonceau(codePanonceau: string): StoreCountryEnum {
        if (this.isFrenchCodePanonceau(codePanonceau)) return StoreCountryEnum.FRANCE;
        if (this.isLuxembourgerCodePanonceau(codePanonceau)) return StoreCountryEnum.LUXEMBOURG;
        if (this.isSpanishCodePanonceau(codePanonceau)) return StoreCountryEnum.ESPAGNE;
        if (this.isPortugueseCodePanonceau(codePanonceau)) return StoreCountryEnum.PORTUGAL;
        if (this.isSlovenianCodePanonceau(codePanonceau)) return StoreCountryEnum.SLOVENIE;
        if (this.isAndorranCodePanonceau(codePanonceau)) return StoreCountryEnum.ANDORRE;
        console.error(`Aucun pays ne correspond au code panonceau ${codePanonceau}`);
        return null;
    }

    public getTextOfCountryEnum(country: StoreCountryEnum): string {
        switch (country) {
            case StoreCountryEnum.FRANCE:
                return 'France';
            case StoreCountryEnum.LUXEMBOURG:
                return 'Luxembourg';
            case StoreCountryEnum.ESPAGNE:
                return 'Espagne';
            case StoreCountryEnum.PORTUGAL:
                return 'Portugal';
            case StoreCountryEnum.SLOVENIE:
                return 'Slovenie';
            case StoreCountryEnum.ANDORRE:
                return 'Andorre';
            default:
                return '';
        }
    }

    public isFrenchCodePanonceau(codePanonceau: string): boolean {
        return !isNaN(+codePanonceau);
    }

    public isLuxembourgerCodePanonceau(codePanonceau: string): boolean {
        return this.regex.luxembourg.codePanonceau.test(codePanonceau);
    }

    public isSpanishCodePanonceau(codePanonceau: string): boolean {
        return this.regex.espagne.codePanonceau.test(codePanonceau);
    }

    public isPortugueseCodePanonceau(codePanonceau: string): boolean {
        return this.regex.portugal.codePanonceau.test(codePanonceau);
    }

    public isSlovenianCodePanonceau(codePanonceau: string): boolean {
        return this.regex.slovenie.codePanonceau.test(codePanonceau);
    }

    public isAndorranCodePanonceau(codePanonceau: string): boolean {
        return this.regex.andorre.codePanonceau.test(codePanonceau);
    }

    public isFrenchPostalCode(postalCode: string): boolean {
        return !isNaN(+postalCode);
    }

    public isLuxembourgPostalCode(postalCode: string): boolean {
        return this.regex.luxembourg.postalCode.test(postalCode);
    }

    public isFrenchStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.FRANCE;
    }

    public isLuxembourgerStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.LUXEMBOURG;
    }

    public isSpanishStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.ESPAGNE;
    }

    public isPortugueseStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.PORTUGAL;
    }

    public isSlovenianStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.SLOVENIE;
    }

    public isAndorranStore(country: StoreCountryEnum): boolean {
        if (country === undefined) return false;
        return country === StoreCountryEnum.ANDORRE;
    }

    public getPostalCodeValidatorsList(codePanonceau: string, isActive: boolean = true): ValidatorFn[] {
        const country: StoreCountryEnum = this.getCountryWithCodePanonceau(codePanonceau);

        const validators: ValidatorFn[] = [];
        switch (country) {
            case StoreCountryEnum.FRANCE:
                validators.push(RxwebValidators.digit());
                validators.push(RxwebValidators.pattern({ expression: { postalCodeFrench: this.getRegexPostalCodeFrench() }}));
                break;
            case StoreCountryEnum.LUXEMBOURG:
                validators.push(RxwebValidators.pattern({ expression: { postalCodeLuxembourger: this.getRegexPostalCodeLuxembourger() }}));
                break;
            case StoreCountryEnum.ESPAGNE:
                validators.push(RxwebValidators.pattern({ expression: { postalCodeSpain: this.getRegexPostalCodeSpain() }}));
                break;
            case StoreCountryEnum.PORTUGAL:
                validators.push(RxwebValidators.pattern({ expression: { postalCodePortuguese: this.getRegexPostalCodePortuguese() }}));
                break;
            case StoreCountryEnum.SLOVENIE:
                validators.push(RxwebValidators.pattern({ expression: { postalCodeSlovenia: this.getRegexPostalCodeSlovenia() }}));
                break;
            case StoreCountryEnum.ANDORRE:
                validators.push(RxwebValidators.pattern({ expression: { postalCodeAndorran: this.getRegexPostalCodeAndorran() }}));
                break;
        }
        if (isActive) validators.push(Validators.required);
        return validators;
    }

    public getPhoneValidatorsList(codePanonceau: string, isActive: boolean = true): ValidatorFn[] {
        const country: StoreCountryEnum = this.getCountryWithCodePanonceau(codePanonceau);

        const validators: ValidatorFn[] = [
            Validators.maxLength(25),
        ];
        switch (country) {
            case StoreCountryEnum.FRANCE:
                validators.push(RxwebValidators.pattern({ expression: { phoneFrench: this.getRegexPhoneFrench() }}));
                break;
            case StoreCountryEnum.LUXEMBOURG:
                validators.push(RxwebValidators.pattern({ expression: { phoneLuxembourger: this.getRegexPhoneLuxembourger() }}));
                break;
            case StoreCountryEnum.ANDORRE:
                validators.push(RxwebValidators.pattern({ expression: { phoneAndorran: this.getRegexPhoneAndorran() }}));
                break;
        }
        if (isActive) validators.push(Validators.required);
        return validators;
    }
}
